import { Component, Input, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { AdminTenantGroupService } from '../../../../services/admin-tenant-group.service';
import { ToastService } from '../../../../services/toast-service';

@Component({
  selector: 'app-edit-group-modal',
  templateUrl: './edit-group-modal.component.html',
  styleUrls: ['./edit-group-modal.component.scss']
})
export class EditGroupModalComponent implements OnInit {
  @Input() group: any;
  @ViewChild('removeMemberModal') removeMemberModal!: TemplateRef<any>;
  
  groupForm!: FormGroup;
  saving = false;
  loadingMembers = false;
  savingMembers = false;
  
  allDomains: any[] = [];
  currentMembers: any[] = [];
  availableDomains: any[] = [];
  selectedDomainIds: number[] = [];
  memberToRemove: any = null;
  private confirmModalRef?: NgbModalRef;

  activeTab = 'details'; // 'details' or 'members'

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private adminService: AdminTenantGroupService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.groupForm = this.fb.group({
      groupID: [this.group?.groupID],
      groupName: [this.group?.groupName || '', [Validators.required, Validators.maxLength(100)]],
      description: [this.group?.description || '', Validators.maxLength(500)]
    });

    this.loadMembers();
  }

  loadMembers(): void {
    this.loadingMembers = true;
    this.adminService.getGroupMembers(this.group.groupID).subscribe({
      next: (response) => {
        if (response?.success) {
          this.currentMembers = response.result?.currentMembers || [];
          this.allDomains = response.result?.allDomains || [];
          this.updateAvailableDomains();
        }
        this.loadingMembers = false;
      },
      error: (error) => {
        console.error('Error loading members:', error);
        this.loadingMembers = false;
      }
    });
  }

  updateAvailableDomains(): void {
    const memberDomainIds = this.currentMembers.map(m => m.domainID);
    this.availableDomains = this.allDomains.filter(d => !memberDomainIds.includes(d.domainID));
  }

  addMembers(): void {
    if (this.selectedDomainIds.length === 0) {
      return;
    }

    // Map selected IDs to domain objects with domainName (from allDomains list)
    const domainsToAdd = this.selectedDomainIds.map(id => {
      const domain = this.allDomains.find(d => d.domainID === id);
      return {
        domainID: id,
        domainName: domain?.domainName || `Domain ${id}`
      };
    });

    this.savingMembers = true;
    this.adminService.addMember(this.group.groupID, domainsToAdd).subscribe({
      next: (response) => {
        if (response?.success) {
          this.toastService.show({
            type: 'success',
            data: [this.translateService.instant('TenantGroupManagement.membersAdded')]
          });
          this.selectedDomainIds = [];
          this.loadMembers();
        }
        this.savingMembers = false;
      },
      error: (error) => {
        console.error('Error adding members:', error);
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('TenantGroupManagement.addMembersFailed')]
        });
        this.savingMembers = false;
      }
    });
  }

  removeMember(domainID: number): void {
    const member = this.currentMembers.find(m => m.domainID === domainID);
    this.memberToRemove = member || { domainID };
    this.confirmModalRef = this.modalService.open(this.removeMemberModal, { centered: true, backdrop: 'static' });
  }

  confirmRemoveMember(): void {
    if (!this.memberToRemove) return;
    const domainID = this.memberToRemove.domainID;

    this.adminService.removeMember(this.group.groupID, domainID).subscribe({
      next: (response) => {
        if (response?.success) {
          this.toastService.show({
            type: 'success',
            data: [this.translateService.instant('TenantGroupManagement.memberRemoved')]
          });
          this.loadMembers();
        }
        this.confirmModalRef?.close();
        this.memberToRemove = null;
      },
      error: (error) => {
        console.error('Error removing member:', error);
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('TenantGroupManagement.removeMemberFailed')]
        });
        this.confirmModalRef?.close();
        this.memberToRemove = null;
      }
    });
  }

  cancelRemoveMember(): void {
    this.confirmModalRef?.close();
    this.memberToRemove = null;
  }

  save(): void {
    if (this.groupForm.invalid) {
      Object.keys(this.groupForm.controls).forEach(key => {
        this.groupForm.controls[key].markAsTouched();
      });
      return;
    }

    this.saving = true;
    const formData = this.groupForm.value;

    this.adminService.updateGroup(formData.groupID, formData).subscribe({
      next: (response) => {
        if (response?.success) {
          this.activeModal.close('updated');
        } else {
          this.toastService.showApiError(response?.errorMessage, this.translateService.instant('TenantGroupManagement.updateFailed'));
          this.saving = false;
        }
      },
      error: (error) => {
        console.error('Error updating group:', error);
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('TenantGroupManagement.updateFailed')]
        });
        this.saving = false;
      }
    });
  }

  cancel(): void {
    this.activeModal.dismiss();
  }
}
